'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, useTransition } from 'react';
import { motion } from 'motion/react';
import { Loader2, Search } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { cn } from '~/utils';

/** Per-character reveal speed and how long a fully-typed suggestion lingers before the next. */
const TYPE_CHAR_MS = 45;
const HOLD_FULL_MS = 1800;
type Props = {
  href: string;
  placeholderPrefix: string;
  suggestions: readonly string[];
  aiSearchLabel: string;
  ariaLabel: string;
  /** `glass` = standalone dark hero pill. `light` = borderless, embedded in a white bar. */
  surface?: 'glass' | 'light';
  className?: string;
  /** When set, submitting calls this with the trimmed value instead of navigating to `href`. */
  onSubmit?: (value: string) => void;
  /** Control rendered at the start of the bar (e.g. the AI mode toggle). */
  leading?: React.ReactNode;
  /** Control rendered just before the submit button (e.g. the chat-history dropdown). */
  trailing?: React.ReactNode;
  /** Seeds the input (e.g. a prompt carried over from another page). */
  initialValue?: string;
};
export const HeaderAiSearchBar: React.FC<Props> = t0 => {
  const $ = _c(65);
  const {
    href,
    placeholderPrefix,
    suggestions,
    aiSearchLabel,
    ariaLabel,
    surface: t1,
    className,
    onSubmit,
    leading,
    trailing,
    initialValue
  } = t0;
  const surface = t1 === undefined ? "glass" : t1;
  const isLight = surface === "light";
  const router = useRouter();
  const [isNavigating, startNavigation] = useTransition();
  const [value, setValue] = useState(initialValue ?? "");
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState("");
  const inputRef = useRef(null);
  const isTyping = value.length > 0;
  const currentSuggestion = suggestions[index] ?? "";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const el = inputRef.current;
      if (!el) {
        return;
      }
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== value) {
    t3 = [value];
    $[1] = value;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== currentSuggestion || $[4] !== isTyping || $[5] !== suggestions.length || $[6] !== typed) {
    t4 = () => {
      if (isTyping || suggestions.length === 0) {
        return;
      }
      if (typed.length < currentSuggestion.length) {
        const id = setTimeout(() => setTyped(currentSuggestion.slice(0, typed.length + 1)), TYPE_CHAR_MS);
        return () => clearTimeout(id);
      }
      const id_0 = setTimeout(() => {
        setTyped("");
        setIndex(i => (i + 1) % suggestions.length);
      }, HOLD_FULL_MS);
      return () => clearTimeout(id_0);
    };
    t5 = [typed, currentSuggestion, isTyping, suggestions.length];
    $[3] = currentSuggestion;
    $[4] = isTyping;
    $[5] = suggestions.length;
    $[6] = typed;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] !== initialValue) {
    t6 = () => {
      if (initialValue && initialValue.length > 0) {
        setValue(initialValue);
      }
    };
    t7 = [initialValue];
    $[9] = initialValue;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== href || $[13] !== onSubmit || $[14] !== router || $[15] !== value) {
    t8 = e => {
      e.preventDefault();
      const trimmed = value.trim();
      if (onSubmit) {
        if (trimmed.length === 0) {
          return;
        }
        onSubmit(trimmed);
        setValue("");
        return;
      }
      const url = trimmed.length > 0 ? `${href}&startPrompt=${encodeURIComponent(trimmed)}` : href;
      startNavigation(() => router.push(url));
    };
    $[12] = href;
    $[13] = onSubmit;
    $[14] = router;
    $[15] = value;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleSubmit = t8;
  let t9;
  if ($[17] !== className || $[18] !== isLight) {
    t9 = cn("flex w-full items-center gap-3 rounded-full text-left text-sm font-semibold lg:gap-4", isLight ? "max-lg:flex-wrap max-lg:items-start max-lg:gap-y-2 bg-transparent py-1 text-primary lg:px-2" : ["max-w-xl bg-white/5 py-2 pl-4 pr-2 text-white", "ring-1 ring-white/15 backdrop-blur-md", "transition-colors focus-within:bg-white/10 focus-within:ring-white/30"], className);
    $[17] = className;
    $[18] = isLight;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => inputRef.current?.focus();
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== leading) {
    t11 = leading && <div className="flex shrink-0 items-center" onClick={_temp}>{leading}</div>;
    $[21] = leading;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = isLight ? "max-lg:mt-2 max-lg:ml-1 max-lg:self-start text-gray-400" : "text-white/70";
  let t13;
  if ($[23] !== t12) {
    t13 = cn("size-5 shrink-0", t12);
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13) {
    t14 = <Search className={t13} strokeWidth={2.5} aria-hidden={true} />;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e_1 => setValue(e_1.target.value);
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const t16 = isLight && "max-lg:min-h-[4.5rem]";
  const t17 = isLight ? "text-primary" : "text-white";
  const t18 = !isTyping && "caret-transparent";
  let t19;
  if ($[28] !== t16 || $[29] !== t17 || $[30] !== t18) {
    t19 = cn("min-w-0 flex-1 resize-none border-0 bg-transparent px-0 py-1 text-sm font-semibold leading-normal lg:text-base", "hide-scrollbar max-h-28 overflow-y-auto", t16, t17, "outline-none ring-0 focus:ring-0", t18);
    $[28] = t16;
    $[29] = t17;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== ariaLabel || $[33] !== t19 || $[34] !== value) {
    t20 = <textarea ref={inputRef} data-testid="ai-chat-hero-input" value={value} rows={1} onChange={t15} onKeyDown={_temp2} className={t19} autoComplete="off" enterKeyHint="search" aria-label={ariaLabel} />;
    $[32] = ariaLabel;
    $[33] = t19;
    $[34] = value;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== isLight || $[37] !== isTyping || $[38] !== placeholderPrefix || $[39] !== typed) {
    t21 = !isTyping && <div className={cn("pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-nowrap text-sm leading-normal lg:text-base", isLight && "max-lg:items-start max-lg:pt-2", isLight ? "text-gray-400" : "text-white/60")} aria-hidden={true}><span className="min-w-0 truncate">{placeholderPrefix} {typed}</span><motion.span animate={{
        opacity: [1, 0.2, 1]
      }} transition={{
        duration: 1.1,
        repeat: Infinity,
        ease: "easeInOut"
      }} className={cn("ml-0.5 h-5 w-0.5 shrink-0 rounded-full", isLight ? "bg-gray-500" : "bg-white/80")} /></div>;
    $[36] = isLight;
    $[37] = isTyping;
    $[38] = placeholderPrefix;
    $[39] = typed;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t20 || $[42] !== t21) {
    t22 = <div className="relative flex min-w-0 flex-1 items-center">{t20}{t21}</div>;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== trailing) {
    t23 = trailing && <div className="flex shrink-0 items-center" onClick={_temp3}>{trailing}</div>;
    $[44] = trailing;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== isLight) {
    t24 = isLight && <span className="basis-full lg:hidden" aria-hidden={true} />;
    $[46] = isLight;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== isNavigating) {
    t25 = isNavigating ? <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden={true} /> : <Search className="size-4 shrink-0" strokeWidth={2.25} aria-hidden={true} />;
    $[48] = isNavigating;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== aiSearchLabel) {
    t26 = <span className="whitespace-nowrap">{aiSearchLabel}</span>;
    $[50] = aiSearchLabel;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== ariaLabel || $[53] !== t25 || $[54] !== t26) {
    t27 = <button type="submit" className="max-lg:order-last max-lg:ml-auto flex h-11 shrink-0 items-center justify-center gap-2 rounded-4xl bg-tertiary px-4 text-sm font-semibold text-white transition-colors hover:bg-tertiary-700 lg:h-16 lg:px-6 lg:text-base" aria-label={ariaLabel}>{t25}{t26}</button>;
    $[52] = ariaLabel;
    $[53] = t25;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== handleSubmit || $[57] !== t11 || $[58] !== t14 || $[59] !== t22 || $[60] !== t23 || $[61] !== t24 || $[62] !== t27 || $[63] !== t9) {
    t28 = <form onSubmit={handleSubmit} className={t9} onClick={t10}>{t11}{t14}{t22}{t23}{t24}{t27}</form>;
    $[56] = handleSubmit;
    $[57] = t11;
    $[58] = t14;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
    $[62] = t27;
    $[63] = t9;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  return t28;
};
function _temp(e_0) {
  return e_0.stopPropagation();
}
function _temp2(e_2) {
  if (e_2.key === "Enter" && !e_2.shiftKey) {
    e_2.preventDefault();
    e_2.currentTarget.form?.requestSubmit();
  }
}
function _temp3(e_3) {
  return e_3.stopPropagation();
}